import { useEffect, useState, useCallback } from "react";
import {
    BarChart3, TrendingUp, Award, IndianRupee, Users, Activity, AlertTriangle, Building2,
    CheckCircle2, RefreshCw,
} from "lucide-react";
import { Bar, Doughnut } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale, LinearScale, BarElement, ArcElement,
    Title, Tooltip, Legend,
} from "chart.js";
import dayjs from "dayjs";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import {
    ENGAGE_A_REFERRER_LEADERBOARD_URL,
    ENGAGE_A_CAMP_ROI_URL,
    ENGAGE_A_ENQUIRY_FUNNEL_URL,
    ENGAGE_A_CORPORATE_RENEWALS_URL,
    ENGAGE_A_INCENTIVES_SUMMARY_URL,
    ENGAGE_A_FOLLOWUP_SUMMARY_URL,
    ENGAGE_A_FOLLOWUP_BY_ASSIGNEE_URL,
} from "../../constants/api";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

// ─── Small subcomponents ────────────────────────────────────────────────────

const StatCard = ({ icon: Icon, label, value, sub, color = "blue" }) => {
    const colorMap = {
        blue:   "bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400",
        green:  "bg-green-50 text-green-600 dark:bg-green-950/40 dark:text-green-400",
        amber:  "bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400",
        rose:   "bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400",
        violet: "bg-violet-50 text-violet-600 dark:bg-violet-950/40 dark:text-violet-400",
    };
    return (
        <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
            <div className="flex items-center gap-3">
                <div className={`p-2 rounded-md ${colorMap[color]}`}>
                    <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</div>
                    {sub && <div className="text-xs text-gray-500 dark:text-gray-400">{sub}</div>}
                </div>
            </div>
        </div>
    );
};

const Section = ({ title, icon: Icon, children }) => (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 dark:border-gray-800">
            {Icon && <Icon className="w-4 h-4 text-gray-500" />}
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        </div>
        <div className="p-4">{children}</div>
    </div>
);

// ─── Main component ────────────────────────────────────────────────────────

const EngageAnalytics = () => {
    const { fetch: apiFetch } = useApiMethods();
    const [loading, setLoading] = useState(true);

    const [leaderboard, setLeaderboard]   = useState([]);
    const [campROI, setCampROI]           = useState([]);
    const [funnel, setFunnel]             = useState({ groups: [], total: 0 });
    const [renewals, setRenewals]         = useState({ summary: {}, buckets: {} });
    const [incentives, setIncentives]     = useState({ totals: {}, by_doctor: [] });
    const [fupSummary, setFupSummary]     = useState({ totals: {}, sentiment_distribution: {}, completion_rate_pct: 0, avg_hours_to_complete: null, by_type: [], by_channel: [] });
    const [fupByAssignee, setFupByAssignee] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [rlb, cro, fnl, rnw, inc, fup, fpa] = await Promise.all([
                apiFetch(ENGAGE_A_REFERRER_LEADERBOARD_URL, { limit: 10 }).catch(() => ({ results: [] })),
                apiFetch(ENGAGE_A_CAMP_ROI_URL).catch(() => ({ results: [] })),
                apiFetch(ENGAGE_A_ENQUIRY_FUNNEL_URL).catch(() => ({ groups: [], total: 0 })),
                apiFetch(ENGAGE_A_CORPORATE_RENEWALS_URL, { days_ahead: 180 }).catch(() => ({ summary: {}, buckets: {} })),
                apiFetch(ENGAGE_A_INCENTIVES_SUMMARY_URL).catch(() => ({ totals: {}, by_doctor: [] })),
                apiFetch(ENGAGE_A_FOLLOWUP_SUMMARY_URL).catch(() => ({ totals: {}, sentiment_distribution: {}, completion_rate_pct: 0, by_type: [], by_channel: [] })),
                apiFetch(ENGAGE_A_FOLLOWUP_BY_ASSIGNEE_URL).catch(() => ({ results: [] })),
            ]);
            setLeaderboard(rlb?.results ?? []);
            setCampROI(cro?.results ?? []);
            setFunnel({ groups: fnl?.groups ?? [], total: fnl?.total ?? 0 });
            setRenewals({ summary: rnw?.summary ?? {}, buckets: rnw?.buckets ?? {} });
            setIncentives({ totals: inc?.totals ?? {}, by_doctor: inc?.by_doctor ?? [] });
            setFupSummary(fup);
            setFupByAssignee(fpa?.results ?? []);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { load(); }, [load]);

    // ── Chart data prep ─────────────────────────────────────────────────

    const enquiryFunnelChart = {
        labels: funnel.groups.map(g => g.group_label),
        datasets: [
            {
                label: 'Total',
                data:  funnel.groups.map(g => g.total),
                backgroundColor: 'rgba(59, 130, 246, 0.7)',
            },
            {
                label: 'Converted',
                data:  funnel.groups.map(g => g.by_status?.converted || 0),
                backgroundColor: 'rgba(34, 197, 94, 0.7)',
            },
        ],
    };

    const referrerBarChart = {
        labels: leaderboard.map(d => `Dr. ${d.doctor_name}`),
        datasets: [
            {
                label: 'Referrals',
                data:  leaderboard.map(d => d.referrals_total),
                backgroundColor: 'rgba(99, 102, 241, 0.7)',
            },
            {
                label: 'Converted',
                data:  leaderboard.map(d => d.referrals_converted),
                backgroundColor: 'rgba(34, 197, 94, 0.7)',
            },
        ],
    };

    const sentimentDist = fupSummary.sentiment_distribution || {};
    const sentimentChart = {
        labels: ['Positive', 'Neutral', 'Negative', 'N/A', 'Not filled'],
        datasets: [{
            data: [
                sentimentDist.positive || 0,
                sentimentDist.neutral || 0,
                sentimentDist.negative || 0,
                sentimentDist.na || 0,
                sentimentDist.unfilled || 0,
            ],
            backgroundColor: [
                'rgba(34, 197, 94, 0.8)',
                'rgba(148, 163, 184, 0.8)',
                'rgba(244, 63, 94, 0.8)',
                'rgba(203, 213, 225, 0.8)',
                'rgba(226, 232, 240, 0.8)',
            ],
            borderWidth: 1,
        }],
    };

    const chartOpts = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } } },
        scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
    };

    const doughnutOpts = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } } },
    };

    // ── Derived numbers ─────────────────────────────────────────────────

    const totalReferrals    = leaderboard.reduce((s, d) => s + (d.referrals_total || 0), 0);
    const totalConverted    = leaderboard.reduce((s, d) => s + (d.referrals_converted || 0), 0);
    const conversionRatePct = totalReferrals ? Math.round(totalConverted * 100 / totalReferrals) : 0;
    const unpaidTotal       = parseFloat(incentives.totals?.unpaid_amount || 0);
    const upcomingCamps     = campROI.filter(c => c.status !== 'completed' && c.status !== 'cancelled').length;

    return (
        <ContainerCard>
            {/* ── Header ───────────────────────────────────────────────── */}
            <div className="flex items-center justify-between mb-4 px-1">
                <div className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-violet-600" />
                    <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Analytics</h1>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                        · executive overview (last 90 days)
                    </span>
                </div>
                <Button variant="outline" size="sm" onClick={load} disabled={loading}>
                    <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
            </div>

            {/* ── Row 1: Headline stat cards ────────────────────────────── */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <StatCard
                    icon={Award}
                    label="Total Referrals (90d)"
                    value={loading ? '…' : totalReferrals}
                    sub={`${totalConverted} converted (${conversionRatePct}%)`}
                    color="violet"
                />
                <StatCard
                    icon={Activity}
                    label="Followup Completion"
                    value={loading ? '…' : `${fupSummary.completion_rate_pct || 0}%`}
                    sub={`avg ${fupSummary.avg_hours_to_complete ?? '—'}h to complete`}
                    color="green"
                />
                <StatCard
                    icon={IndianRupee}
                    label="Unpaid Incentives"
                    value={loading ? '…' : `₹${unpaidTotal.toLocaleString('en-IN')}`}
                    sub={`${incentives.totals?.unpaid_count || 0} referrals awaiting`}
                    color="rose"
                />
                <StatCard
                    icon={Users}
                    label="Active Camps"
                    value={loading ? '…' : upcomingCamps}
                    sub={`${campROI.length} in period`}
                    color="amber"
                />
            </div>

            {/* ── Row 2: Enquiry funnel + Sentiment donut ─────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
                <div className="lg:col-span-2">
                    <Section title="Enquiry sources — total vs converted" icon={TrendingUp}>
                        <div style={{ height: 260 }}>
                            {loading ? <div className="text-sm text-gray-500">Loading…</div> :
                             funnel.groups.length === 0 ? <div className="text-sm text-gray-500 italic">No enquiries in the last 90 days.</div> :
                             <Bar data={enquiryFunnelChart} options={chartOpts} />
                            }
                        </div>
                    </Section>
                </div>
                <div>
                    <Section title="Followup outcome sentiment" icon={Activity}>
                        <div style={{ height: 260 }}>
                            {loading ? <div className="text-sm text-gray-500">Loading…</div> :
                             <Doughnut data={sentimentChart} options={doughnutOpts} />
                            }
                        </div>
                    </Section>
                </div>
            </div>

            {/* ── Row 3: Top referrers bar chart ──────────────────────── */}
            <div className="mb-4">
                <Section title="Top referring doctors — referrals vs converted" icon={Award}>
                    <div style={{ height: 300 }}>
                        {loading ? <div className="text-sm text-gray-500">Loading…</div> :
                         leaderboard.length === 0 ? <div className="text-sm text-gray-500 italic">No referral activity in this window.</div> :
                         <Bar data={referrerBarChart} options={chartOpts} />
                        }
                    </div>
                </Section>
            </div>

            {/* ── Row 4: Follow-up performance by assignee ──────────── */}
            <div className="mb-4">
                <Section title="Follow-up performance by team member" icon={Users}>
                    {loading ? <div className="text-sm text-gray-500">Loading…</div> :
                     fupByAssignee.length === 0 ? <div className="text-sm text-gray-500 italic">No follow-up activity in this period.</div> :
                     <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs text-gray-500 border-b border-gray-100 dark:border-gray-800">
                                <th className="py-2 font-medium">Team member</th>
                                <th className="py-2 font-medium text-right">Assigned</th>
                                <th className="py-2 font-medium text-right">Completed</th>
                                <th className="py-2 font-medium text-right">Overdue</th>
                                <th className="py-2 font-medium text-right">Rate</th>
                                <th className="py-2 font-medium text-right">Positive %</th>
                                <th className="py-2 font-medium text-right">Avg hours</th>
                            </tr>
                        </thead>
                        <tbody>
                            {fupByAssignee.map(u => (
                                <tr key={u.user_id} className="border-b border-gray-100 dark:border-gray-800 last:border-b-0">
                                    <td className="py-2 text-gray-900 dark:text-gray-100">{u.user_name}</td>
                                    <td className="py-2 text-right text-gray-700 dark:text-gray-300">{u.assigned}</td>
                                    <td className="py-2 text-right text-green-700 dark:text-green-400 font-medium">{u.completed}</td>
                                    <td className={`py-2 text-right ${u.overdue > 0 ? 'text-rose-600 font-medium' : 'text-gray-400'}`}>{u.overdue}</td>
                                    <td className="py-2 text-right text-gray-700 dark:text-gray-300">{u.completion_rate_pct}%</td>
                                    <td className="py-2 text-right text-gray-700 dark:text-gray-300">{u.positive_sentiment_pct}%</td>
                                    <td className="py-2 text-right text-gray-700 dark:text-gray-300">{u.avg_hours_to_complete ?? '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                     </table>
                    }
                </Section>
            </div>

            {/* ── Row 5: Corporate renewals + Incentives by doctor ───── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
                <Section title="Corporate contract renewals" icon={Building2}>
                    <div className="grid grid-cols-4 gap-2 mb-3">
                        <div className="p-2 rounded bg-rose-50 dark:bg-rose-950/30 text-center">
                            <div className="text-xs text-gray-500">Expired</div>
                            <div className="text-lg font-bold text-rose-700 dark:text-rose-300">{renewals.summary?.expired ?? 0}</div>
                        </div>
                        <div className="p-2 rounded bg-rose-50 dark:bg-rose-950/30 text-center">
                            <div className="text-xs text-gray-500">Critical (≤30d)</div>
                            <div className="text-lg font-bold text-rose-700 dark:text-rose-300">{renewals.summary?.critical ?? 0}</div>
                        </div>
                        <div className="p-2 rounded bg-amber-50 dark:bg-amber-950/30 text-center">
                            <div className="text-xs text-gray-500">Soon (31-90d)</div>
                            <div className="text-lg font-bold text-amber-700 dark:text-amber-300">{renewals.summary?.soon ?? 0}</div>
                        </div>
                        <div className="p-2 rounded bg-gray-50 dark:bg-gray-800 text-center">
                            <div className="text-xs text-gray-500">Distant</div>
                            <div className="text-lg font-bold text-gray-700 dark:text-gray-300">{renewals.summary?.distant ?? 0}</div>
                        </div>
                    </div>
                    {['expired', 'critical', 'soon'].flatMap(bucket =>
                        (renewals.buckets?.[bucket] || []).map(c => (
                            <div key={c.corporate_id} className="flex items-center justify-between text-sm py-1.5 border-b border-gray-100 dark:border-gray-800 last:border-b-0">
                                <span className="text-gray-900 dark:text-gray-100">{c.company_name}</span>
                                <span className={
                                    c.days_until_expiry < 0 ? 'text-rose-600 font-semibold text-xs' :
                                    c.days_until_expiry <= 30 ? 'text-rose-600 text-xs' :
                                    'text-amber-600 text-xs'
                                }>
                                    {c.days_until_expiry < 0
                                        ? `Expired ${Math.abs(c.days_until_expiry)}d ago`
                                        : `${c.days_until_expiry}d left`}
                                </span>
                            </div>
                        ))
                    )}
                    {['expired', 'critical', 'soon'].every(b => (renewals.buckets?.[b] || []).length === 0) && !loading && (
                        <div className="text-sm text-gray-500 italic text-center py-4">
                            No corporate contracts need attention in the next 90 days.
                        </div>
                    )}
                </Section>

                <Section title="Incentive payment status" icon={IndianRupee}>
                    <div className="flex justify-around mb-4 text-center">
                        <div>
                            <div className="text-xs text-gray-500">Paid</div>
                            <div className="text-xl font-bold text-green-700 dark:text-green-400">
                                ₹{parseFloat(incentives.totals?.paid_amount || 0).toLocaleString('en-IN')}
                            </div>
                            <div className="text-xs text-gray-500">{incentives.totals?.paid_count || 0} referrals</div>
                        </div>
                        <div className="border-l border-gray-200 dark:border-gray-700" />
                        <div>
                            <div className="text-xs text-gray-500">Unpaid</div>
                            <div className="text-xl font-bold text-rose-700 dark:text-rose-400">
                                ₹{parseFloat(incentives.totals?.unpaid_amount || 0).toLocaleString('en-IN')}
                            </div>
                            <div className="text-xs text-gray-500">{incentives.totals?.unpaid_count || 0} referrals</div>
                        </div>
                    </div>
                    <div className="max-h-56 overflow-y-auto">
                        {incentives.by_doctor.slice(0, 8).map((d) => (
                            <div key={d.doctor_id} className="flex items-center justify-between text-sm py-1.5 border-b border-gray-100 dark:border-gray-800 last:border-b-0">
                                <span className="text-gray-900 dark:text-gray-100 truncate max-w-[60%]">Dr. {d.doctor_name}</span>
                                <span className="whitespace-nowrap text-xs text-gray-500">
                                    <span className="text-green-700">₹{parseFloat(d.paid_amount).toLocaleString('en-IN')}</span>
                                    {' / '}
                                    <span className="text-rose-700">₹{parseFloat(d.unpaid_amount).toLocaleString('en-IN')}</span>
                                </span>
                            </div>
                        ))}
                    </div>
                </Section>
            </div>

            {/* ── Row 6: Camp ROI table ──────────────────────────────── */}
            <div className="mb-4">
                <Section title="Health camp ROI" icon={Users}>
                    {loading ? <div className="text-sm text-gray-500">Loading…</div> :
                     campROI.length === 0 ? <div className="text-sm text-gray-500 italic">No camps to report.</div> :
                     <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs text-gray-500 border-b border-gray-100 dark:border-gray-800">
                                <th className="py-2 font-medium">Camp</th>
                                <th className="py-2 font-medium">Focus</th>
                                <th className="py-2 font-medium text-right">Footfall</th>
                                <th className="py-2 font-medium text-right">Enquiries</th>
                                <th className="py-2 font-medium text-right">Converted</th>
                                <th className="py-2 font-medium text-right">Conv. rate</th>
                                <th className="py-2 font-medium text-right">Spend</th>
                                <th className="py-2 font-medium text-right">₹ / conversion</th>
                            </tr>
                        </thead>
                        <tbody>
                            {campROI.map(c => (
                                <tr key={c.camp_id} className="border-b border-gray-100 dark:border-gray-800 last:border-b-0">
                                    <td className="py-2 text-gray-900 dark:text-gray-100 truncate max-w-xs">{c.camp_name}</td>
                                    <td className="py-2 text-gray-700 dark:text-gray-300">{c.specialty_focus || '—'}</td>
                                    <td className="py-2 text-right text-gray-700 dark:text-gray-300">{c.actual_footfall}/{c.expected_footfall}</td>
                                    <td className="py-2 text-right text-gray-900 dark:text-gray-100">{c.enquiries_captured}</td>
                                    <td className="py-2 text-right text-green-700 dark:text-green-400 font-medium">{c.converted_patients}</td>
                                    <td className="py-2 text-right text-gray-700 dark:text-gray-300">{c.conversion_rate_pct}%</td>
                                    <td className="py-2 text-right text-gray-700 dark:text-gray-300">₹{parseFloat(c.actual_spend || 0).toLocaleString('en-IN')}</td>
                                    <td className="py-2 text-right text-gray-700 dark:text-gray-300">
                                        {c.cost_per_conversion ? `₹${parseFloat(c.cost_per_conversion).toLocaleString('en-IN')}` : '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                     </table>
                    }
                </Section>
            </div>
        </ContainerCard>
    );
};

export default EngageAnalytics;
